import dayjs from "dayjs";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { FormHeader } from "../../components/Core/AppText/AppText";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import TimeReportCalendar from "../../components/Shared/TimeReportCalendar/TimeReportCalender";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import useQueryState from "../../hooks/useQueryState";
import Customer from "../../models/Customer";
import { Application } from "../../models/Job";
import { ORDER_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useJobs } from "../../networking/job.hooks";
import { useTimeReports } from "../../networking/time-report.hooks";
import { jobFromTo } from "../../utils/date.time.utils";
import { jobRoute } from "../../utils/route.utils";

const CustomerJobsTab: React.FC<{ customer: Customer }> = ({ customer }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [order, setOrder] = useQueryState<SORT_ORDER | undefined>("order");
	const [sort, setSort] = useQueryState<ORDER_SORTING_COLUMN>("col");

	const { jobList } = useJobs({
		customerId: customer.id,
		page: pageNumber,
		pageSize: PAGE_SIZE,
		expand: ["applications.consultant"],
	});
	const jobs = jobList?.jobs ?? [];
	const totalOrderCount = jobList?.totalCount ?? 0;

	const { data: timeReportsData, isLoading: loading } = useTimeReports({
		customer: customer.id,
		startDate: dayjs().subtract(6, "months").format(DATE_TIME_FORMAT_TYPES.DATE),
		endDate: dayjs().add(2, "months").format(DATE_TIME_FORMAT_TYPES.DATE),
		expand: "shift,job.customer,job",
	});
	const customersTimeReports = timeReportsData?.timeReports ?? [];

	const handleSortColumn = async (sort?: ORDER_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const handleSortingOrder = () => {
		if (!order) {
			setOrder(SORT_ORDER.ASC);
		} else if (order === SORT_ORDER.ASC) {
			setOrder(SORT_ORDER.DESC);
		} else if (order === SORT_ORDER.DESC) {
			setOrder(SORT_ORDER.ASC);
		}
	};

	const getConsultantNames = (applications: Application[]) => {
		return applications && applications.length > 0
			? applications.map((app) => (
					<React.Fragment key={app.id}>
						<span>{`${app.consultant?.firstName} ${app.consultant?.lastName}`}</span>
						<br />
					</React.Fragment>
			  ))
			: "-";
	};

	return (
		<Styles>
			{loading ? (
				<LoadingIndicator />
			) : (
				<Flex direction="row">
					<Flex direction="column" width="440px" margin="0 2rem 0 0 ">
						<FormHeader margin="0 0 1rem 0">{t("SHIFTS_BOOKED")}</FormHeader>
						<TimeReportCalendar timeReports={customersTimeReports} />
					</Flex>
					<Flex direction="column">
						<FormHeader margin="0 0 1rem 0">{t("JOBS")}</FormHeader>
						<TableLayout>
							<table>
								<thead>
									<tr>
										<th onClick={() => handleSortColumn(ORDER_SORTING_COLUMN.ORDER_NO)}>
											ID
											<SortIcon
												sortType={
													sort === ORDER_SORTING_COLUMN.ORDER_NO ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th onClick={() => handleSortColumn(ORDER_SORTING_COLUMN.DATE)}>
											{t("FROM")} - {t("TO")}
											<SortIcon
												sortType={sort === ORDER_SORTING_COLUMN.DATE ? order : SORT_ORDER.DEFAULT}
											/>
										</th>
										<th
											onClick={() => handleSortColumn(ORDER_SORTING_COLUMN.HOURS)}
											style={{ minWidth: 75 }}
										>
											{t("HOURS_SHORT")}
											<SortIcon
												sortType={sort === ORDER_SORTING_COLUMN.HOURS ? order : SORT_ORDER.DEFAULT}
											/>
										</th>
										<th>{t("CONSULTANT")}</th>
										<th
											onClick={() => handleSortColumn(ORDER_SORTING_COLUMN.CREATED)}
											style={{ minWidth: 150 }}
										>
											{t("CREATED")}
											<SortIcon
												sortType={
													sort === ORDER_SORTING_COLUMN.CREATED ? order : SORT_ORDER.DEFAULT
												}
											/>
										</th>
										<th>{t("STATUS")}</th>
									</tr>
								</thead>
								<tbody>
									{jobs.map((job) => (
										<tr key={job.id} onClick={() => navigate(jobRoute(job.id))}>
											<td>{job.orderNumber}</td>
											<td>{jobFromTo(job.startDate as string, job.endDate as string)}</td>
											<td>{job.noOfHours}</td>
											<td>{getConsultantNames(job.applications ?? [])}</td>
											<td>{moment(job.metadata.created).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
											<td>{t(("job.jobStatus." + job.status.toLowerCase()) as any)}</td>
										</tr>
									))}
								</tbody>
							</table>
						</TableLayout>
						{totalOrderCount > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalOrderCount}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
					</Flex>
				</Flex>
			)}
		</Styles>
	);
};

export default CustomerJobsTab;

const Styles = styled.div`
	padding: 1rem;
`;
